import type { Dispatch, SetStateAction } from "react";

import {
  INPROGRESS_STATUS_CODE,
  NOT_STARTED_STATUS_CODE,
  OVERDUE_STATUS_CODE,
  PROFICIENCY_THRESHOLDS,
  resettableStatusCodes,
  type LearnerScore,
  type AssessmentData,
  type AssignmentScores,
  type ProficiencyDistribution,
  type ProficiencyLevel,
  type CategoryScore,
  type TopicScore,
} from "@/constants/assessmentsReport.constants";

/** Converts a numeric score (0–100) to a proficiency level key. */
export const getProficiencyFromScore = (
  averageScore: number | null | undefined
): ProficiencyLevel | null => {
  if (averageScore == null) return null;
  if (averageScore >= PROFICIENCY_THRESHOLDS.PROFICIENCIES) return "advanced";
  if (averageScore >= PROFICIENCY_THRESHOLDS.GROWTH_OPPORTUNITIES) return "intermediate";
  return "foundational";
};

/** Formats a duration (seconds as string/number) to "HH:MM:SS". */
export const formatTimeHHMMSS = (timeSpent: string | number | null | undefined): string => {
  if (timeSpent == null || timeSpent === "") return "--";
  const totalSeconds = Math.round(parseFloat(String(timeSpent)));
  if (isNaN(totalSeconds) || totalSeconds < 0) return "--";
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const calculateLearnerScores = (
  scores: Array<{ details: { earnedPoints: number; possiblePoints: number } }>
): number | null => {
  if (!scores.length) return null;
  const earned = scores.reduce((acc, s) => acc + s.details.earnedPoints, 0);
  const possible = scores.reduce((acc, s) => acc + s.details.possiblePoints, 0);
  if (possible === 0) return null;
  return Math.round((earned / possible) * 100);
};

/**
 * Merges reporting scores + due dates into assessment learnerStatuses by directoryUuid.
 * O(n) via Map lookup.
 */
export const addReportingScoreToLearner = (
  assessmentData: AssessmentData,
  reportingData: AssignmentScores
): AssessmentData => {
  const learnerScores = reportingData?.assignmentScores?.[0]?.learnerScores ?? [];

  const learnerDataMap = new Map(
    learnerScores.map(
      ({ directoryUuid, scores, dueAt, completedAt, timeSpent, examInstanceId }) => [
        directoryUuid,
        { scores, dueDate: dueAt, completedAt, timeSpent, examInstanceId },
      ]
    )
  );

  return {
    ...assessmentData,
    learnerStatuses: assessmentData.learnerStatuses.map(learner => {
      const {
        scores = [],
        dueDate,
        completedAt,
        timeSpent,
        examInstanceId = "",
      } = learnerDataMap.get(learner.directoryUuid) ?? {};
      return { ...learner, scores, dueDate, completedAt, timeSpent, examInstanceId };
    }),
  };
};

/** Adds averageScore, proficiency, resettable, and timeSpent fields to raw learner rows. */
export const enrichLearnerRows = (reports: LearnerScore[]): LearnerScore[] => {
  if (!reports?.length) return [];
  return reports.map(report => {
    const resettable = resettableStatusCodes.includes(report.statusCode);
    const completedScores = (report.scores ?? []).filter(s => !s.notApplicable);
    const averageScore = calculateLearnerScores(completedScores);
    const proficiency = getProficiencyFromScore(averageScore);
    const timeSpentSeconds = report.timeSpent ? parseFloat(report.timeSpent) : null;
    const timeSpentFormatted = formatTimeHHMMSS(report.timeSpent);
    return {
      ...report,
      averageScore,
      proficiency,
      resettable,
      timeSpentSeconds,
      timeSpentFormatted,
    };
  });
};

/** Filters learner rows to those with a completedAt timestamp. */
export const filterCompletedLearnerRows = (rows: LearnerScore[]): LearnerScore[] =>
  rows.filter(r => r.completedAt != null);

/** Computes overall average score across an array of numeric scores (returns "--" when empty). */
export const calculateAssessmentScores = (scores: number[]): number | "--" => {
  const valid = scores.filter(s => typeof s === "number");
  if (!valid.length) return "--";
  return Math.round(valid.reduce((acc, s) => acc + s, 0) / valid.length);
};

/** Computes human-readable average time spent across completed learner rows. */
export const calculateAverageTimeSpent = (rows: LearnerScore[]): string => {
  const seconds = rows
    .map(r => (r.timeSpent ? parseInt(r.timeSpent, 10) : NaN))
    .filter(s => !isNaN(s));
  if (!seconds.length) return "--";
  const avg = seconds.reduce((a, b) => a + b, 0) / seconds.length;
  const days = Math.floor(avg / 86400);
  const hours = Math.floor((avg % 86400) / 3600);
  const minutes = Math.floor((avg % 3600) / 60);
  const secs = Math.round(avg % 60);
  if (avg >= 86400) return `${days}d ${hours}h ${minutes}m`;
  if (avg >= 3600) return `${hours}h ${minutes}m`;
  return `${minutes}m ${secs}s`;
};

/** Calculates percentage distribution across proficiency + status buckets. */
export const calculateProficiencyDistribution = (rows: LearnerScore[]): ProficiencyDistribution => {
  const total = rows.length;
  if (total === 0)
    return { foundational: 0, intermediate: 0, advanced: 0, inProgress: 0, notStarted: 0 };

  const counts = { foundational: 0, intermediate: 0, advanced: 0, inProgress: 0, notStarted: 0 };
  rows.forEach(r => {
    if (r.proficiency === "foundational") counts.foundational++;
    else if (r.proficiency === "intermediate") counts.intermediate++;
    else if (r.proficiency === "advanced") counts.advanced++;
    else if (r.statusCode === INPROGRESS_STATUS_CODE || r.status === "started") counts.inProgress++;
    else counts.notStarted++;
  });

  return {
    foundational: Math.round((counts.foundational / total) * 100),
    intermediate: Math.round((counts.intermediate / total) * 100),
    advanced: Math.round((counts.advanced / total) * 100),
    inProgress: Math.round((counts.inProgress / total) * 100),
    notStarted: Math.round((counts.notStarted / total) * 100),
  };
};

/** Applies column-level filters (status, proficiency, programmingLanguage) to learner rows. */
export const applyLearnerFilters = (
  rows: LearnerScore[],
  filters: {
    statusCode?: { value: string[] | null };
    proficiency?: { value: string[] | null };
    programmingLanguage?: { value: string[] | null };
  }
): LearnerScore[] =>
  rows.filter(learner => {
    if (filters.statusCode?.value?.length) {
      if (!filters.statusCode.value.includes(learner.statusCode)) return false;
    }
    if (filters.proficiency?.value?.length) {
      if (!learner.proficiency || !filters.proficiency.value.includes(learner.proficiency))
        return false;
    }
    if (filters.programmingLanguage?.value?.length) {
      if (
        !learner.programmingLanguage ||
        !filters.programmingLanguage.value.includes(learner.programmingLanguage)
      )
        return false;
    }
    return true;
  });

type SetFilters = Dispatch<SetStateAction<Record<string, { value: unknown; matchMode: string }>>>;

/** Toggles a proficiency/status filter button; clicking the active key clears all filters. */
export const handleProficiencyFilterClick = (
  key: string,
  activeFilterKey: string | null,
  setActiveFilterKey: Dispatch<SetStateAction<string | null>>,
  setFilters: SetFilters,
  matchModeIn: string
): void => {
  if (activeFilterKey === key) {
    setActiveFilterKey(null);
    setFilters(prev => ({
      ...prev,
      statusCode: { value: null, matchMode: matchModeIn },
      proficiency: { value: null, matchMode: matchModeIn },
    }));
    return;
  }

  setActiveFilterKey(key);

  const statusCodeMap: Record<string, string[]> = {
    inProgress: [INPROGRESS_STATUS_CODE, "started"],
    notStarted: [NOT_STARTED_STATUS_CODE, OVERDUE_STATUS_CODE, "not_started"],
  };

  if (["foundational", "intermediate", "advanced"].includes(key)) {
    setFilters(prev => ({
      ...prev,
      proficiency: { value: [key], matchMode: matchModeIn },
      statusCode: { value: null, matchMode: matchModeIn },
    }));
  } else if (statusCodeMap[key]) {
    setFilters(prev => ({
      ...prev,
      statusCode: { value: statusCodeMap[key], matchMode: matchModeIn },
      proficiency: { value: null, matchMode: matchModeIn },
    }));
  }
};

type ScoreEntry = {
  category: string;
  topic: string;
  path?: string;
  score: number;
  possiblePoints: number;
};

type ScoredRow = {
  scores?: Array<{
    notApplicable?: boolean;
    topic?: { names: string[]; path?: string };
    details?: { earnedPoints: number; possiblePoints: number };
  }>;
};

/** Flattens applicable scores from all learner rows into a flat list of score entries. */
export const filterAllScores = (rows: ScoredRow[]): ScoreEntry[] => {
  const entries: ScoreEntry[] = [];
  rows.forEach(learner => {
    (learner.scores ?? []).forEach(score => {
      if (!score.notApplicable && score.topic && score.details) {
        entries.push({
          category: score.topic.names[0] ?? "",
          topic: score.topic.names[1] ?? "",
          path: score.topic.path,
          score: score.details.earnedPoints,
          possiblePoints: score.details.possiblePoints,
        });
      }
    });
  });
  return entries;
};

/** Aggregates flat score entries into per-category and per-topic averages. */
export const calculateAverageScores = (
  entries: ScoreEntry[]
): { categoryScoresAverage: CategoryScore[]; topicScoresAverage: Record<string, TopicScore[]> } => {
  type Tally = { totalScore: number; count: number; path?: string };
  const catMap = new Map<string, Tally>();
  const topicMap = new Map<string, Map<string, Tally>>();

  entries.forEach(({ category, topic, path, score, possiblePoints }) => {
    if (!catMap.has(category)) catMap.set(category, { totalScore: 0, count: 0 });
    const c = catMap.get(category)!;
    c.totalScore += score;
    c.count += possiblePoints;

    if (!topicMap.has(category)) topicMap.set(category, new Map());
    const byTopic = topicMap.get(category)!;
    if (!byTopic.has(topic)) byTopic.set(topic, { totalScore: 0, count: 0, path });
    const t = byTopic.get(topic)!;
    t.totalScore += score;
    t.count += possiblePoints;
    if (!t.path && path) t.path = path;
  });

  const categoryScoresAverage: CategoryScore[] = [];
  catMap.forEach((tally, category) => {
    categoryScoresAverage.push({
      category,
      averageScore: Math.round((tally.totalScore / tally.count) * 100),
    });
  });

  const topicScoresAverage: Record<string, TopicScore[]> = {};
  topicMap.forEach((topics, category) => {
    const scores: TopicScore[] = [];
    topics.forEach((tally, topic) => {
      scores.push({
        topic,
        path: tally.path,
        averageScore: Math.round((tally.totalScore / tally.count) * 100),
      });
    });
    topicScoresAverage[category] = scores;
  });

  return { categoryScoresAverage, topicScoresAverage };
};
